/* recs-router.js — the ONE smart client file for owned recs.
 *
 * Reads each <recs-rail>'s injected rec_config ruleset, builds the first-party
 * scenario (device / visitor / utm / cart / purchase_history / customer +
 * server-known collection & product context), evaluates rules top-down
 * (first match wins) walking the fallback chain until a source yields items,
 * renders card-product tiles, applies the always-on hard filters, wires
 * promo-aware cart signage from the live discount config, and emits
 * rec_impression / rec_click for RPS measurement.
 *
 * The section is dumb; this is where the decisions live. evaluateRuleset() is
 * mirrored byte-for-byte in decision/evaluator.py (unit-tested there).
 */
(function () {
  "use strict";
  if (window.__recsRouterLoaded) return;
  window.__recsRouterLoaded = true;

  var MIN_ITEMS = 3;                 // choice-overload floor (Baymard: 4-6 ideal)
  var RV_KEY = "ob_recently_viewed";
  var PURCH_KEY = "ob_purchased_ygroups";
  var FIRST_KEY = "ob_first_seen";
  var pageSeen = { handles: {}, ygroups: {} };   // dedupe across all rails on the page

  // ---- consent (functional tier). No consent => session-only, skip visitor/history ----
  function hasConsent() {
    // Cookie Information's own cookie first: it holds the visitor's stored answer from the
    // first byte, while Shopify's API only learns it once the banner script syncs on load —
    // asking the API too early returned "no" for consented visitors (2026-10-07, measured).
    // Same rule Shopify applies: consented = statistics AND marketing approved.
    try {
      var cm = document.cookie.match(/(?:^|; )CookieInformationConsent=([^;]*)/);
      if (cm) {
        var ok = JSON.parse(decodeURIComponent(cm[1])).consents_approved || [];
        return ok.indexOf("cookie_cat_statistic") !== -1 && ok.indexOf("cookie_cat_marketing") !== -1;
      }
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.customerPrivacy &&
          typeof Shopify.customerPrivacy.userCanBeTracked === "function") {
        return Shopify.customerPrivacy.userCanBeTracked();
      }
    } catch (e) {}
    // DEV fallback: no banner present -> allow (documented). Prod wires to the real banner.
    return true;
  }
  function lsGet(k) { try { return hasConsent() ? window.localStorage.getItem(k) : null; } catch (e) { return null; } }
  function lsSet(k, v) { try { if (hasConsent()) window.localStorage.setItem(k, v); } catch (e) {} }

  function jsonArr(s) { try { var a = JSON.parse(s); return Array.isArray(a) ? a : []; } catch (e) { return []; } }

  // ---- scenario ----
  function persistUtm() {
    try {
      var p = new URLSearchParams(window.location.search);
      var src = p.get("utm_source");
      if (src) sessionStorage.setItem("ob_utm_source", src.toLowerCase());
      var content = p.get("utm_content");
      if (content) sessionStorage.setItem("ob_utm_content", content.toLowerCase());
    } catch (e) {}
  }
  function getUtm() { try { return sessionStorage.getItem("ob_utm_source"); } catch (e) { return null; } }

  function visitorType() {
    var seen = lsGet(FIRST_KEY);
    if (!seen) { lsSet(FIRST_KEY, String(Date.now())); return "new"; }
    // returning if the first-seen is older than this session (>30 min ago)
    return (Date.now() - parseInt(seen, 10) > 30 * 60 * 1000) ? "returning" : "new";
  }

  function fetchJSON(url) { return fetch(url, { headers: { Accept: "application/json" } }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }); }
  function fetchText(url) { return fetch(url).then(function (r) { return r.ok ? r.text() : ""; }).catch(function () { return ""; }); }
  // A ?view= render MUST be fetched WITHOUT Accept: application/json. Shopify ignores the
  // view param when that header is present and serves its OWN product JSON instead
  // ({"product":{...}} — no `complements` key), so fetchJSON returned a valid-but-wrong
  // object and every cart-rail lookup resolved to ZERO handles. That, not the data, is why
  // the cart rail was signage-only through T2 — it could not have filled at ANY data
  // quality. Fetch as text and parse. (recs-card already used fetchText, which is why the
  // PLP/PDP rails were unaffected.) Found 2026-07-16 by replaying the router's own fetch.
  function fetchViewJSON(url) {
    return fetch(url).then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) { if (!t) return null; try { return JSON.parse(t); } catch (e) { return null; } })
      .catch(function () { return null; });
  }

  function mostCommonSize(cart) {
    var counts = {}, best = null, bestN = 0;
    (cart && cart.items || []).forEach(function (it) {
      (it.options_with_values || []).forEach(function (o) {
        if ((o.name || "").toLowerCase().indexOf("size") !== -1) {
          counts[o.value] = (counts[o.value] || 0) + it.quantity;
          if (counts[o.value] > bestN) { bestN = counts[o.value]; best = o.value; }
        }
      });
    });
    return best;
  }

  // ---- promo eligibility is PER TAG GROUP, not per cart ----
  // The buy-3-save-15 promo is scoped by `Discount_<group>` product tags and applies
  // WITHIN a group: "buy any 3 BRAS", never "buy any 3 items" (Sebastian 2026-07-17).
  // Only ~50% of the catalogue carries any Discount_ tag, so an ineligible cart must get
  // NO signage. v0 counted raw cart.item_count against a fictional mix-and-match promo:
  // it promised 15% for any 2 items and then recommended briefs to a bra cart, which
  // could never trigger it. A rail that recommends non-qualifying items is worse than no
  // rail — it makes a promise the checkout won't keep.
  //
  // cart.__groups = {group: units} is attached upstream (needs a per-item metafield read),
  // so this stays a pure function of what it's given.
  function promoGap(cart, disc) {
    var groups = (cart && cart.__groups) || null;
    if (!groups) return null;
    var promos = (disc && disc.quantity_promos) || [];
    for (var i = 0; i < promos.length; i++) {
      var p = promos[i];
      if (!p.min_qty || p.scope !== "tag_group") continue;
      for (var g in groups) {
        if (!Object.prototype.hasOwnProperty.call(groups, g)) continue;
        if (groups[g] === p.min_qty - 1) {
          var label = (p.labels && p.labels[g]) || g;
          return { group: g, label: label, percent: p.percent, need: 1, promo: p };
        }
      }
    }
    return null;
  }

  function cartState(cart, disc) {
    if (!cart || cart.item_count === 0) return "empty";
    if (promoGap(cart, disc)) return "near_promo";
    if (disc && disc.free_ship_threshold_cents) {
      var gap = disc.free_ship_threshold_cents - cart.total_price;
      if (gap > 0 && gap <= (disc.free_ship_band_cents || 15000)) return "near_freeship";
    }
    return "has_items";
  }

  function buildScenario(cfg, cart, disc) {
    var s = Object.assign({}, cfg.scenario_server || {});
    s.device = window.matchMedia("(max-width: 749px)").matches ? "mobile" : "desktop";
    s.visitor = visitorType();
    s.utm_source = getUtm();
    s.cart_items = (cart && cart.item_count > 0) ? ">0" : "0";
    s.cart_state = cartState(cart, disc);
    // which Discount_ group the cart is one item short of — scopes BOTH the signage copy
    // and the rail's candidates, so we only ever recommend items that actually qualify
    var gap = promoGap(cart, disc);
    s.promo_group = gap ? gap.group : null;
    s.__promo = gap;
    // Which experiment arm this visitor is in, from the same <html> attribute the on/off
    // switch uses. Absent -> "", which matches no variant condition, so every rule left on
    // "Any" serves everyone and the control group IS today's behaviour.
    s.variant = (document.documentElement.getAttribute("data-recs-variant") || "").trim().toLowerCase();
    s.purchase_history = jsonArr(lsGet(PURCH_KEY)).length > 0;
    s.inferred_size = mostCommonSize(cart);
    if (!hasConsent()) { s.visitor = "any"; s.purchase_history = "any"; }  // session-only
    return s;
  }

  // ---- PURE: rule evaluation (mirrored in decision/evaluator.py) ----
  function matchCond(cond, s) {
    for (var key in cond) {
      if (!Object.prototype.hasOwnProperty.call(cond, key)) continue;
      var want = cond[key];
      if (want === "any" || want == null) continue;
      var have = s[key];
      // "any" scenario value = UNKNOWN (consent-off). Privacy-correct: unknown does NOT
      // satisfy a targeted condition -> rule skipped, falls through to default.
      if (key === "utm_source") {
        var arr = (Array.isArray(want) ? want : [want]).map(function (x) { return String(x).toLowerCase(); });
        if (!have || arr.indexOf(String(have).toLowerCase()) === -1) return false;
      } else if (Array.isArray(want)) {
        if (want.map(String).indexOf(String(have)) === -1) return false;
      } else if (typeof want === "boolean") {
        if (have !== want) return false;
      } else {
        if (String(have) !== String(want)) return false;
      }
    }
    return true;
  }
  function evaluateRuleset(ruleset, s) {
    var rules = (ruleset && ruleset.rules) || [];
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (r.enabled === false) continue;
      if (matchCond(r["if"] || {}, s)) {
        return Object.assign({ rule_id: r.id }, r.serve);
      }
    }
    return Object.assign({ rule_id: "default" }, (ruleset && ruleset.default && ruleset.default.serve) || {});
  }

  // ---- source resolution -> ordered handles (or {native:intent}) ----
  // `utm_or:X` = "the UTM-implied collection if any, else X" (rec_config
  // _meta.special_collection_tokens). No utm->collection map exists anywhere in the
  // project, so today the token ALWAYS resolves to its fallback X. Resolved here rather
  // than invented, so Dana's rules stay readable exactly as she authored them and the map
  // has one obvious place to land later.
  function resolveCollectionToken(c) {
    c = c || "self";
    if (c.indexOf("utm_or:") === 0) return c.slice(7) || "self";
    return c;
  }

  // ---- per-handle metafield cache ----
  // Each ?view=recs-json is a real network round-trip and the drawer re-reads the same
  // handles on every refresh. Cache by handle: the cart's contents rarely change between
  // refreshes, and an uncached re-read was a big part of the drawer's render cost.
  var viewCache = {};
  function productMeta(handle) {
    if (viewCache[handle]) return viewCache[handle];
    viewCache[handle] = fetchViewJSON("/products/" + encodeURIComponent(handle) + "?view=recs-json");
    return viewCache[handle];
  }

  // Attach {group: units} to the cart, from each line's Discount_ tags. Needed because
  // /cart.js carries no tags — the metafield view is the only way to see them.
  function withCartGroups(cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) { if (cart) cart.__groups = {}; return Promise.resolve(cart); }
    return Promise.all(items.map(function (it) { return productMeta(it.handle); }))
      .then(function (metas) {
        var g = {};
        metas.forEach(function (m, i) {
          var qty = items[i].quantity || 1;
          ((m && m.discount_groups) || []).forEach(function (grp) {
            g[grp] = (g[grp] || 0) + qty;   // a product may sit in >1 group; count in each
          });
        });
        cart.__groups = g;
        return cart;
      })
      .catch(function () { if (cart) cart.__groups = {}; return cart; });
  }
  function srcSig(src) {
    if (!src || !src.type) return null;
    if (src.type === "collection_top") return "collection_top:" + resolveCollectionToken(src.collection);
    return src.type;
  }
  function waitRivo(ms) {
    return new Promise(function (res, rej) {
      var t0 = Date.now();
      (function tick() {
        if (window.RivoAPI && window.RivoAPI.products && window.RivoJS) return res();
        if (Date.now() - t0 > ms) return rej(new Error("rivo-absent"));
        setTimeout(tick, 60);
      })();
    });
  }
  function rivoIdsToHandles(ids) {
    ids = (ids || []).filter(Boolean);
    if (!ids.length) return Promise.resolve([]);
    return Promise.resolve(window.RivoAPI.products(ids)).then(function (r) {
      var by = {};
      ((r && r.products) || []).forEach(function (p) { if (p && p.handle) by[String(p.remote_id)] = p.handle; });
      return ids.map(function (i) { return by[String(i)]; }).filter(Boolean);
    }).catch(function () { return []; });
  }
  function resolveOne(src, cfg, s, cart) {
    if (!src || !src.type) return Promise.resolve([]);
    var sig = srcSig(src);
    var server = cfg.sources_server || {};
    if (src.type === "recently_viewed") {
      var rv = jsonArr(lsGet(RV_KEY)).filter(function (h) { return h !== s.product_handle; });
      return Promise.resolve(rv);
    }
    if (src.type === "native_related" || src.type === "native_complementary") {
      var intent = src.type === "native_complementary" ? "complementary" : "related";
      var pid = s.product_id || (cart && cart.items && cart.items.length ? cart.items[cart.items.length - 1].product_id : null);
      if (!pid) return Promise.resolve([]);
      return Promise.resolve({ native: intent, product_id: pid });
    }
    // PDP slots: curated means THIS product's own `upsell_styled_with` (the cart branch
    // below unions the CART's lines instead, which is a different question). Without this
    // branch curated_upsell fell through to the server-prerender lookup and returned [],
    // so the similar rail's fallback could never fire (Fifi 2026-10-09).
    if (src.type === "curated_upsell" && cfg.slot !== "cart-rail") {
      var selfH = s.product_handle;
      if (!selfH) return Promise.resolve([]);
      return productMeta(selfH).then(function (j) {
        var list = (j && j.curated && j.curated.length) ? j.curated : ((j && j.complements) || []);
        return list.filter(function (h) { return h && h !== selfH; });
      }).catch(function () { return []; });
    }
    if (src.type === "curated_upsell" && cfg.slot === "cart-rail") {
      // union of cart items' complements (metafields readable only via recs-json view;
      // the view merges curated-first, then the pipeline's recs.complements). Cached per
      // handle — the drawer re-resolves on every refresh.
      //
      // NEAR-PROMO EXCEPTION: the promo is per-group ("buy any 3 BRAS"), so a cart of 2
      // bras is completed by a THIRD BRA — complements are bottoms and can never satisfy
      // it (filterCells correctly dropped all of them, leaving an empty rail). So when a
      // group is one item short, also offer that item's `similar` (same YCRF + gender =
      // same category), and let the group filter keep only what genuinely qualifies.
      // This is NOT the Baymard substitute trap: that warns against inviting a SWAP of
      // what's in the cart. Here the cart keeps everything and gains a third item — the
      // completion the customer was just promised. Both lists come from the SAME cached
      // fetch, so this costs nothing extra.
      var items = (cart && cart.items) || [];
      return Promise.all(items.map(function (it) { return productMeta(it.handle); }))
        .then(function (rs) {
          var out = [];
          var add = function (h) { if (h && out.indexOf(h) === -1) out.push(h); };
          rs.forEach(function (j) { (j && j.complements || []).forEach(add); });
          if (s && s.promo_group) rs.forEach(function (j) { (j && j.similar || []).forEach(add); });
          // MIRROR_CURATED_FILL (2026-10-12). EU/UK/US carry NO Shopify complementary-products
          // data (0 metafields vs DK's 1,701), so the union above is empty there and the rail
          // would hide on every cart. `custom.upsell_styled_with` answers the same "goes with"
          // question, is curated, and covers 98% of products on every store. APPENDED, never
          // ahead of the complements, so a DK cart resolves exactly what it resolves today.
          if (out.length < MIN_ITEMS) rs.forEach(function (j) { (j && j.curated || []).forEach(add); });
          return out;
        });
    }
    // Buy-it-again. The name is historical: the source is the SERVER-rendered order list
    // (Liquid customer.orders, emitted by rv-variant-gate into [data-rv-config]), not Rivo —
    // Rivo's order proxy has no product handles and took 4.1s live (DK 2026-10-05). Until
    // this branch existed the source returned [] and the rail silently showed fallbacks.
    // Rivo-backed memory sources (2026-10-07): the homepage shows what Rivo's own panel shows.
    //   rivo_recently_viewed  Rivo's list in localStorage (product ids) -> handles via RivoAPI.products
    //   rivo_favorites        RivoJS.getFavoriteProducts() (signed-in members)
    // Both wait for Rivo's deferred bundle; order is kept as Rivo stores it (newest first).
    if (src.type === "rivo_recently_viewed" || src.type === "rivo_favorites") {
      return waitRivo(8000).then(function () {
        if (src.type === "rivo_favorites") {
          return Promise.resolve(window.RivoJS.getFavoriteProducts()).then(function (r) {
            var ps = (r && r.products) || [];
            var hs = ps.map(function (p) { return p.handle; }).filter(Boolean);
            return hs.length === ps.length ? hs : rivoIdsToHandles(ps.map(function (p) { return p.remote_id || p.product_id || p.id; }));
          });
        }
        return rivoIdsToHandles(jsonArr(lsGet("rivo_recently_viewed_product_ids")).slice(0, 12));
      }).catch(function () { return []; });
    }
    if (src.type === "rivo_orders" || src.type === "purchase_history") {
      try {
        var el = document.querySelector("[data-rv-config]") || document.getElementById("rv-config");
        var ord = (el && JSON.parse(el.textContent).orders) || [];
        return Promise.resolve(ord.map(function (o) { return o.handle; }).filter(function (h) { return h && h !== s.product_handle; }));
      } catch (e) { return Promise.resolve([]); }
    }
    if (server[sig] && server[sig].length) return Promise.resolve(server[sig].slice());
    // A collection_top pointing somewhere OTHER than `self` has no server prerender — the
    // rail cannot know which collection to bake in until the ruleset is evaluated against
    // the session's UTM, which happens here, client-side. Read it through the same
    // ?view= JSON path the cart rail uses for product metafields.
    // fetchViewJSON, NOT fetchJSON: an `Accept: application/json` header makes Shopify
    // ignore ?view= and hand back its own JSON — 200 OK, our keys silently absent. That
    // exact trap cost the cart rail every lookup it ever made (BUILD_LOG Block 18).
    if (src.type === "collection_top") {
      var handle = resolveCollectionToken(src.collection);
      if (handle && handle !== "self") {
        return fetchViewJSON("/collections/" + encodeURIComponent(handle) + "?view=recs-json")
          .then(function (j) { return (j && j.top_products) || []; })
          .catch(function () { return []; });
      }
    }
    return Promise.resolve([]);
  }
  function resolveChain(serve, cfg, s, cart) {
    var chain = [serve.source].concat(serve.fallbacks || []).filter(Boolean);  // drop undefined sources
    var depth = 0;
    function tryNext() {
      if (depth >= chain.length) return Promise.resolve({ handles: [], native: null, depth: depth, source_type: "none" });
      var src = chain[depth];
      return resolveOne(src, cfg, s, cart).then(function (res) {
        var d = depth; depth++;
        if (res && res.native) return { handles: [], native: res, depth: d, source_type: src.type };
        // memory sources: ANY items win — never skip 1-2 recently-viewed items for a
        // fallback (which then dedupes against sibling rails and can land on zero).
        if (res && res.length > 0 && /^(recently_viewed|rivo_orders|purchase_history|rivo_recently_viewed|rivo_favorites)$/.test(src.type)) return { handles: res, native: null, depth: d, source_type: src.type };
        if (res && res.length >= MIN_ITEMS) return { handles: res, native: null, depth: d, source_type: src.type };
        if (res && res.length > 0 && depth >= chain.length) return { handles: res, native: null, depth: d, source_type: src.type };
        return tryNext();
      });
    }
    return tryNext();
  }

  // ---- rendering (on the theme's real <flickity-slider>) ----
  function parseCells(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var host = doc.querySelector("[data-recs-native-cells]") || doc.body;
    return Array.prototype.slice.call(host.querySelectorAll(".carousel__cell.recs-tile, .recs-tile"));
  }
  function fetchCellsForHandles(handles, max, overfetch) {
    // 2026-09-28 PERF: `max * 2` cost 12 product-page renders for a 6-tile rail. The x2
    // headroom only earns its keep where sibling rails dedupe against each other (PDP);
    // the cart drawer is a single rail, so it fetches exactly what it renders.
    // Measured on live DK 2026-09-28: fetching exactly `max` left the drawer with 4 tiles
    // of 6 — filterCells drops a couple (already-seen, wrong promo group). `max + 3` refills
    // reliably at 9 requests instead of 12. The real fix is one batched section render for
    // all tiles (the native path already does that); this is the cheap 25% in the meantime.
    var take = overfetch === false ? max + 3 : max * 2;
    return Promise.all(handles.slice(0, take).map(function (h) {
      return fetchText("/products/" + encodeURIComponent(h) + "?view=recs-card").then(function (t) { return parseCells(t)[0] || null; });
    })).then(function (cells) { return cells.filter(Boolean); });
  }
  function fetchNativeCells(native, max) {
    // Ask Shopify for MORE than we intend to show. Its related-products list is unfiltered:
    // for a men's jacket it returned 8, of which our own filters (colourway duplicates, the
    // PDP gender scope, items already shown) left 2 — a two-tile rail that looks like a
    // mistake. Shopify caps the endpoint at 10; take all of them and let the filters cut it
    // back to `max` (measured 2026-10-08 on DK staging).
    var ask = Math.min(10, Math.max(max * 2, 10));
    var url = "/recommendations/products?section_id=recs-rail&product_id=" + native.product_id +
              "&intent=" + native.native + "&limit=" + ask;
    return fetchText(url).then(function (html) { return parseCells(html); });
  }
  // the theme's image observer only watches nodes present at page load; our injected
  // cells aren't observed -> force the data-srcset/data-src swap so images actually load.
  function hydrateImages(root) {
    Array.prototype.slice.call(root.querySelectorAll("img[data-srcset], img[data-src]")).forEach(function (img) {
      if (!img.getAttribute("srcset") && img.getAttribute("data-srcset")) img.setAttribute("srcset", img.getAttribute("data-srcset"));
      if (!img.getAttribute("src") && img.getAttribute("data-src")) img.setAttribute("src", img.getAttribute("data-src"));
      img.removeAttribute("loading");
    });
  }
  // Force the theme's flickity-slider to initialize NOW, on EVERY rail, so all rails
  // cycle identically (the element's own trigger is a lazy IntersectionObserver that
  // fires unreliably for below-fold/injected rails -> inconsistent arrows vs scroll).
  // rAF lets layout settle; a retry + resize covers the 0-width first-paint case.
  // Let the theme's flickity-slider custom element initialise itself the SAME way it
  // does for the site's own carousels (its connectedCallback sets up a lazy
  // IntersectionObserver). We do NOT force-init — forcing raced the element's own
  // upgrade/observer and produced the inconsistency. For freshly-injected client rails,
  // re-inserting the element (below) triggers connectedCallback so it self-observes.
  // Force EVERY rail's flickity-slider to init the same way, so all rails cycle
  // identically. Gate on window.FlickitySlider (the theme's class) being defined so we
  // never race the custom-element upgrade (the earlier race cause). Poll until ready.
  // static rail = nothing to scroll. Flickity's own deterministic signal: BOTH nav
  // buttons disabled (during normal cycling at least one is enabled). Tag it so CSS
  // hides the arrows -> sparse rails show none; >4-item rails keep arrows + drag.
  function updateStaticState(el) {
    try {
      var btns = el.querySelectorAll(".flickity-prev-next-button");
      var allDisabled = btns.length === 2 && btns[0].disabled && btns[1].disabled;
      // microscroll case: Flickity "scrollable" by a few px -> arrows enabled but
      // clicking moves nothing perceptible. Treat <16px of travel as static too.
      var negligible = false;
      if (el.flkty && el.flkty.size) {
        var travel = (el.flkty.slideableWidth || 0) - (el.flkty.size.innerWidth || 0);
        negligible = travel < 16;
      }
      el.classList.toggle("recs-static", allDisabled || negligible);
    } catch (e) {}
  }
  function doInit(el) {
    try {
      if (el && !el.hasInitialized && typeof el.initFlickity === "function") el.initFlickity();
      if (el && el.flkty && el.flkty.resize) setTimeout(function () {
        try { el.flkty.resize(); } catch (e) {}
        updateStaticState(el);
      }, 60);
      else if (el) setTimeout(function () { updateStaticState(el); }, 120);
    } catch (e) {}
  }
  // rails can become scrollable/unscrollable when the viewport changes
  var resizeTimer = null;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      Array.prototype.slice.call(document.querySelectorAll("recs-rail flickity-slider")).forEach(updateStaticState);
    }, 200);
  });
  // Use customElements.whenDefined — resolves EXACTLY when the theme defines the element,
  // however long its JS takes to load (polling timed out on heavy pages). Every rail then
  // inits the same way -> consistent carousel behavior.
  function initSlider(sliderEl) {
    if (!sliderEl || !window.customElements) { doInit(sliderEl); return; }
    customElements.whenDefined("flickity-slider").then(function () { doInit(sliderEl); }).catch(function () {});
  }
  // Build the slider as an HTML STRING and inject via innerHTML, so the theme's
  // <flickity-slider> custom element upgrades WITH its cells present — exactly like a
  // server-rendered rail. (document.createElement upgrades the element BEFORE cells are
  // appended, leaving its internal this.slider null so Flickity never inits — the bug
  // behind rebuilt rails not getting the carousel.)
  function buildSliderHTML(uid, cells, config) {
    var cellsHTML = cells.map(function (c) { return c.outerHTML; }).join("");
    var cfg = JSON.stringify(config || {});   // double-quoted JSON, safe inside a single-quoted attr
    return '<flickity-slider class="featured-collection"><div class="d-flex">' +
           '<div class="slider-buttons--wrapper">' +
           '<div id="Slider-' + uid + '" class="flickity__slider" role="list" data-flickity-config=\'' + cfg + '\'>' +
           cellsHTML +
           '</div></div></div></flickity-slider>';
  }
  // filter a set of candidate cell nodes -> kept ordered subset (hard filters).
  // `seen` scopes cross-rail dedupe (page rails share pageSeen; the cart drawer gets a
  // fresh scope each refresh so it can re-render on cart change).
  // Size-intel is a PREFERENCE, not a hard filter: pass 1 keeps size-matched tiles,
  // pass 2 backfills the rest. (Hard-filtering emptied whole rails cross-gender —
  // e.g. a womens XXS item in cart nuked every MENS rail, whose tiles never carry XXS.)
  // `relaxed` = memory rails (recently_viewed): a memory feature must be STABLE —
  // it skips in-cart / just-purchased exclusions and cross-rail dedupe (its own
  // fresh scope), so tiles don't appear/vanish as the cart or sibling rails change.
  // Discovery rails keep the full exclusion stack.
  function filterCells(cells, s, cfg, serve, seen, relaxed) {
    seen = relaxed ? { handles: {}, ygroups: {} } : (seen || pageSeen);
    var cartHandles = (window.__recsCartHandles || []);
    var purchased = jsonArr(lsGet(PURCH_KEY));   // ygroups
    var exclusions = cfg.exclusions || [];
    var inferred = s.inferred_size, gender = s.product_gender;
    var max = serve.max_items || cfg.max_items || 8, kept = [];
    // When the signage promises "add 1 more <group> to save 15%", every tile MUST be in
    // that Discount_ group — otherwise the rail recommends items that cannot trigger the
    // discount it just promised (a bra cart was being offered briefs). Hard filter, not a
    // preference: an unqualifying tile here is a broken promise, not a weaker suggestion.
    var promoGroup = (serve.signage === "promo" && s.promo_group) ? s.promo_group : null;
    function pass(strictSize) {
      for (var i = 0; i < cells.length && kept.length < max; i++) {
        var t = cells[i];
        var h = t.getAttribute("data-recs-handle"), g = t.getAttribute("data-recs-gender"),
            yg = t.getAttribute("data-recs-ygroup"), avail = t.getAttribute("data-recs-available") === "1",
            sizes = (t.getAttribute("data-recs-sizes") || "").split(",").filter(Boolean);
        if (h === s.product_handle) continue;                                       // no self-rec
        if (!avail) continue;                                                       // stock filter
        if (promoGroup) {                                                           // must actually qualify
          var dg = t.getAttribute("data-recs-discount") || "";                      // comma-WRAPPED
          if (dg.indexOf("," + promoGroup + ",") === -1) continue;                  // exact membership
        }
        if (!relaxed && cartHandles.indexOf(h) !== -1) continue;                    // exclude in-cart (discovery rails only)
        if (exclusions.indexOf(h) !== -1) continue;                                 // config exclusions
        if (!relaxed && yg && purchased.indexOf(yg) !== -1) continue;               // exclude just-purchased (discovery rails only)
        if (seen.handles[h]) continue;                                              // dedupe (own scope when relaxed)
        if (yg && seen.ygroups[yg]) continue;                                       // dedupe siblings
        if (!relaxed && gender && g && g !== gender && g !== "unisex" && cfg.slot.indexOf("pdp") === 0) continue; // gender scope on PDP
        if (strictSize && inferred && sizes.length && sizes.indexOf(inferred) === -1 && sizes.indexOf("ONESIZE") === -1) continue; // size preference
        kept.push(t);
        seen.handles[h] = 1; if (yg) seen.ygroups[yg] = 1;
      }
    }
    // memory rails (recently viewed, favourites, buy again) show the shopper's own list in its
    // own order — no size reordering, which dropped items past the cap (2026-10-07)
    if (relaxed) { pass(false); return kept; }
    pass(true);                          // size-matched tiles first
    if (kept.length < max) pass(false);  // then backfill regardless of size
    return kept;
  }

  // ---- promo-aware cart signage (reads LIVE cart x configured discount thresholds) ----
  function renderSignage(rail, serve, cart, disc, s) {
    var box = rail.querySelector("[data-recs-signage]");
    if (!box || !serve.signage || serve.signage === "none" || !cart) { if (box) box.hidden = true; return false; }
    // Store's own Shopify money format when the config carries it (DK: "{{amount_with_comma_separator}} DKK"),
    // else the legacy symbol-prefix. Never hardcode "$" onto a non-USD amount.
    var money = function (c) {
      var f = disc.money_format, a = (c / 100).toFixed(2);
      if (!f) return (disc.currency_symbol || "$") + a;
      return f.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, a.replace(".", ","))
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(c / 100)))
              .replace(/\{\{\s*amount\s*\}\}/, a);
    };
    var html = "";
    if (serve.signage === "promo") {
      // Count the GROUP, never cart.item_count. The promo is "buy any 3 bras", so a cart
      // of 2 bras + 1 sock is NOT one item from anything. v0 measured the whole cart
      // against a fictional mix-and-match promo and told people they were one item away
      // when they weren't — the progress bar was measuring the wrong thing entirely.
      var gap = s && s.__promo;
      if (gap) {
        var have = gap.promo.min_qty - gap.need;
        var pct = Math.min(100, Math.round((have / gap.promo.min_qty) * 100));
        html = '<div class="recs-signage recs-signage--promo"><span class="recs-signage__msg">' +
               "Add " + gap.need + " more " + gap.label + " to save " + gap.percent + "%" +
               '</span><span class="recs-signage__bar"><i style="width:' + pct + '%"></i></span></div>';
      }
    } else if (serve.signage === "freeship" && disc.free_ship_threshold_cents) {
      var gap = disc.free_ship_threshold_cents - cart.total_price;
      var fpct = Math.min(100, Math.round((cart.total_price / disc.free_ship_threshold_cents) * 100));
      html = '<div class="recs-signage recs-signage--freeship"><span class="recs-signage__msg">' +
             (gap > 0 ? ("You're " + money(gap) + " from free shipping") : "You've earned free shipping!") +
             '</span><span class="recs-signage__bar"><i style="width:' + fpct + '%"></i></span></div>';
    }
    if (html) { box.innerHTML = html; box.hidden = false; return true; } else { box.hidden = true; return false; }
  }

  // ---- pixel events ----
  function scenarioHash(s) {
    var str = [s.device, s.visitor, s.utm_source, s.cart_state, s.purchase_history, s.collection_type, s.customer].join("|");
    var h = 0; for (var i = 0; i < str.length; i++) { h = (h * 31 + str.charCodeAt(i)) | 0; }
    return "sc_" + (h >>> 0).toString(16);
  }
  function emit(name, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
    if (typeof window.gtag === "function") window.gtag("event", name, payload);
  }
  function railTiles(rail) { return Array.prototype.slice.call(rail.querySelectorAll(".recs-tile")); }
  function wireEvents(rail, meta) {
    var handles = railTiles(rail).map(function (t, i) { return { product: t.getAttribute("data-recs-handle"), position: i + 1 }; });
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { emit("rec_impression", Object.assign({ products: handles }, meta)); obs.disconnect(); }
      });
    }, { threshold: 0.2 });
    io.observe(rail);
    rail.__recsMeta = meta;             // read by the quick-add handler (rec_add_to_cart)
    rail.addEventListener("click", function (ev) {
      var tile = ev.target.closest && ev.target.closest(".recs-tile");
      if (!tile) return;
      emit("rec_click", Object.assign({ product: tile.getAttribute("data-recs-handle"),
        position: railTiles(rail).indexOf(tile) + 1 }, meta));
    });
  }

  // ---- per-rail orchestration ----
  // ---- A/B switch ----------------------------------------------------------------
  // One attribute on <html>, set by the experiment tool before paint:
  //   (absent) / "on"  -> everything as today. ABSENT IS THE DEFAULT, so a page where the
  //                       experiment never runs is untouched — fail-safe by construction.
  //   "off"            -> every rail off.   "off:<slot>[,<slot>]" -> those slots off.
  // Read live on every call rather than cached at boot: the drawer DOM is replaced on every
  // cart change, and a cached answer would outlive the page state it was read from.
  function variantOff(slot) {
    var v = (document.documentElement.getAttribute("data-recs-variant") || "").trim();
    if (!v || v === "on") return false;
    if (v === "off") return true;
    if (v.indexOf("off:") === 0) {
      var names = v.slice(4).split(",");
      for (var i = 0; i < names.length; i++) { if (names[i].trim() === slot) return true; }
    }
    return false;
  }

  function initRail(rail, cart, disc, seen) {
    var cfgEl = rail.querySelector("[data-recs-config]");
    if (!cfgEl) return Promise.resolve();
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return Promise.resolve(); }
    var region = rail.querySelector("[data-recs-slider-region]");
    if (!region) return Promise.resolve();
    // Off for this visitor: leave the markup inert and spend nothing on it — no ruleset, no
    // tile fetches, no Flickity. The CSS has already hidden it; this is what stops the
    // control group paying for a rail it cannot see.
    if (variantOff(cfg.slot)) {
      rail.setAttribute("data-recs-empty", "1");
      rail.setAttribute("data-recs-ready", "1");
      return Promise.resolve();
    }
    var uid = cfg.uid || rail.getAttribute("data-section-id");
    var isDrawer = rail.hasAttribute("data-recs-cart-drawer");
    var s = buildScenario(cfg, cart, disc);
    var serve = evaluateRuleset(cfg.ruleset, s);
    var max = serve.max_items || cfg.max_items || 8;

    var head = rail.querySelector("[data-recs-heading]");
    if (head && serve.heading && !head.hasAttribute("data-recs-heading-fixed")) head.textContent = serve.heading;

    var signageShown = renderSignage(rail, serve, cart, disc, s);

    var meta = { slot: cfg.slot, rule_id: serve.rule_id, scenario_hash: scenarioHash(s) };

    var serverSig = srcSig(serve.source || {});
    var existingSlider = region.querySelector("flickity-slider");
    var existingCells = existingSlider ? Array.prototype.slice.call(existingSlider.querySelectorAll(".carousel__cell")) : [];
    // adopt the server pre-render ONLY when the matched rule's source is exactly what Liquid rendered
    var canAdopt = serverSig && serverSig === cfg.prerender_source && existingCells.length > 0;
    // 2026-09-28: the heading paints from the matched rule BEFORE the products resolve, so
    // a client-hydrated rail flashed a fallback heading ("Recently viewed" in the drawer)
    // over an empty region for a beat. Hide it until commit() knows it has tiles — but only
    // when there is no server pre-render to show, or we'd flicker a filled rail instead.
    if (!existingCells.length) {
      var headElInit = rail.querySelector(".recs-rail__head");
      if (headElInit) headElInit.style.display = "none";
    }

    function done(sourceType, depth) {
      meta.source_type = sourceType; meta.chain_depth = depth;
      rail.setAttribute("data-recs-ready", "1");
      if (isDrawer) preloadQuickAdd(rail);
      wireEvents(rail, meta);
    }
    function commit(candidates, sourceType, depth) {
      var relaxed = /^(recently_viewed|rivo_orders|purchase_history|rivo_recently_viewed|rivo_favorites)$/.test(sourceType);
      var kept = filterCells(candidates, s, cfg, serve, seen, relaxed);
      // MIN 3, the same rule the cart rail and the admin help text already promise: "under 3
      // and the carousel hides itself". A one- or two-tile carousel reads as broken — on DK
      // staging 2026-10-08 the men's jacket's Similar styles came back with ONE tile after
      // the colourway and gender filters, under a heading, which looks like a defect rather
      // than a thin result. Treat too-few as none and show nothing at all.
      if (kept.length < 3) kept = [];
      // strict rails (the homepage's personal rails) never show a fallback under a heading that
      // describes their own source; an empty page rail hides its whole section, or the section's
      // padding leaves a blank band (2026-10-05 audit)
      if (cfg.strict_source && depth > 0) kept = [];
      if (!kept.length && !isDrawer) {
        var secEl = rail.closest(".shopify-section");
        if (secEl) secEl.style.display = "none";
      }
      // A heading with no products under it reads as broken (DK staging, 2026-09-22:
      // "COMPLETE YOUR ORDER" orphaned above Cool Earth at the near-promo step).
      var headEl = rail.querySelector(".recs-rail__head");
      if (!kept.length) {
        region.innerHTML = "";
        if (headEl) headEl.style.display = "none";
        // 2026-09-28 (Sebastian, live DK): signage with NO products under it is a promise
        // the shopper cannot act on — "Add 1 more bottom to save 15%" above an empty rail.
        // Previously the drawer kept the rail visible for signage alone; now the signage
        // goes with the products. No tiles, no promise.
        var signBox = rail.querySelector("[data-recs-signage]");
        if (signBox) { signBox.hidden = true; signBox.innerHTML = ""; }
        rail.setAttribute("data-recs-empty", "1"); rail.setAttribute("data-recs-ready", "1"); return;
      }
      if (headEl) headEl.style.display = "";
      rail.removeAttribute("data-recs-empty");
      // ALWAYS rebuild via innerHTML so EVERY rail (server or client) initialises the
      // theme's Flickity identically. The server-slider "adopt" fast path was the source
      // of the inconsistency (some adopted sliders never got Flickity); the innerHTML
      // rebuild is the proven-reliable path. Content is the same, so re-render is invisible.
      region.innerHTML = buildSliderHTML(uid, kept, cfg.flickity_config);
      var slider = region.querySelector("flickity-slider");
      hydrateImages(slider);
      initSlider(slider);
      done(sourceType, depth);
    }

    if (canAdopt) { commit(existingCells, serve.source.type, 0); return Promise.resolve(); }

    // FILL UP across the chain, for the Similar-styles family only (Fifi 2026-10-09).
    // Shopify's related list is capped at 10 and for OB it is mostly the same style in other
    // colours: on the US core shorty all 8 candidates shared one ygroup, the colourway
    // dedupe left 1, and the rail hid itself on a product with 18 colourways. Choosing the
    // first source that clears MIN cannot rescue that — curated upsell_styled_with carries
    // ~2 refs, also under MIN. So a short source appends instead of ending the walk.
    // ALLOW-LISTED on purpose: Recently viewed / buy-it-again must never be padded out with
    // related products under their own heading, and the cart rail keeps first-match-wins.
    var FILL_SOURCES = { metafield_similar: 1, native_related: 1 };
    var canFill = !isDrawer && cfg.strict_source !== true && !!FILL_SOURCES[(serve.source || {}).type];
    if (!canFill) {
      return resolveChain(serve, cfg, s, cart).then(function (res) {
        if (res.native) return fetchNativeCells(res.native, max).then(function (cells) { commit(cells, "native_" + res.native.native, res.depth); });
        if (res.handles.length) return fetchCellsForHandles(res.handles, max, !isDrawer).then(function (cells) { commit(cells, res.source_type, res.depth); });
        commit([], res.source_type, res.depth);
      });
    }
    var chain = [serve.source].concat(serve.fallbacks || []).filter(Boolean);
    var acc = [], usedTypes = [], step = 0;
    function materialise(src) {
      return resolveOne(src, cfg, s, cart).then(function (res) {
        if (res && res.native) {
          return fetchNativeCells(res, max).then(function (cells) { return { cells: cells, type: "native_" + res.native }; });
        }
        var hs = (res || []).filter(Boolean);
        if (!hs.length) return { cells: [], type: src.type };
        return fetchCellsForHandles(hs, max, !isDrawer).then(function (cells) { return { cells: cells, type: src.type }; });
      }).catch(function () { return { cells: [], type: src.type }; });
    }
    // Trial-filter on a THROWAWAY seen map: filterCells records what it keeps, and probing
    // with the shared page map would make the real commit think its own tiles were already
    // used by another rail.
    function enough() {
      return filterCells(acc, s, cfg, serve, { handles: {}, ygroups: {} }, false).length >= MIN_ITEMS;
    }
    function walk() {
      if (step >= chain.length) return Promise.resolve();
      var src = chain[step++];
      return materialise(src).then(function (r) {
        if (r.cells.length) { acc = acc.concat(r.cells); usedTypes.push(r.type); }
        if (enough()) return;
        return walk();
      });
    }
    return walk().then(function () {
      commit(acc, usedTypes.join("+") || "none", Math.max(0, usedTypes.length - 1));
    });
  }

  // ---- SSR tile images: fetch them when the drawer actually opens ----
  // The server-rendered tiles ship data-src, not src. Measured 2026-10-05 on a cold DEV page
  // load with the drawer NEVER opened: 6 eager tiles pulled 123 KB, 22% of the page, for a
  // carousel nobody had asked to see — the 09-28 per-pageview bill again, paid in images.
  // Two cheaper fixes failed when measured: loading="lazy" still fetched all 6 (a closed
  // drawer sits just off the right edge, inside Chrome's load threshold), and
  // content-visibility:auto did not defer them either while collapsing the rail to 40px.
  // This cannot live in an inline <script> in the drawer: the theme replaces the drawer's
  // inner DOM wholesale, and scripts inserted via innerHTML never execute — the first
  // attempt looked like it worked only because an empty SVG placeholder reports 300x150,
  // which a naturalWidth check happily counts as "an image". It belongs here, where
  // bindSSR already re-runs on every drawer re-render.
  // Safe to gate on JS at all: a cart DRAWER only opens through JavaScript, so a visitor
  // without it never sees this rail. Everything that carries meaning — tiles, names, prices,
  // the promo bar — is in the HTML regardless.
  var recsImgsUnlocked = false;
  function swapRailImages() {
    // RE-QUERY every time. The first version captured the NodeList in a closure and swapped
    // a detached node: the theme replaces the drawer's inner DOM when it opens, so by the
    // time the trigger fired, the tiles on screen were different elements and stayed blank.
    var ns = document.querySelectorAll('recs-rail[data-recs-ssr] img[data-src]');
    for (var i = 0; i < ns.length; i++) {
      var n = ns[i];
      var ownRail = n.closest && n.closest("recs-rail");
      if (ownRail && variantOff(ownRail.getAttribute("data-slot"))) continue;   // A/B: off -> no image fetch
      if (n.dataset.srcset) n.setAttribute("srcset", n.dataset.srcset);
      n.setAttribute("src", n.dataset.src);
      n.removeAttribute("data-src");
    }
  }
  function unlockRailImages() { recsImgsUnlocked = true; swapRailImages(); revealWhenComplete(); }

  // Reveal the rail ONLY once every tile's image has actually decoded. Sebastian's rule
  // (2026-10-07): a rail with grey boxes looks buggy, no rail at all does not. So the CSS
  // keeps it hidden and this is the single place that can show it — and it only does so when
  // the whole thing is real. An image that 404s, a swap that never fires, JS that never
  // binds: all end with the attribute unset and the shopper seeing a clean cart.
  function revealWhenComplete() {
    var rail = document.querySelector("recs-rail[data-recs-ssr]");
    if (!rail) return;
    var imgs = Array.prototype.slice.call(rail.querySelectorAll("img"));
    if (!imgs.length) return;
    var deadline = Date.now() + 6000;      // slow connection, not a broken rail
    var check = function () {
      var fresh = document.querySelector("recs-rail[data-recs-ssr]");
      if (!fresh) return;                                   // drawer re-rendered; a new pass will run
      var all = Array.prototype.slice.call(fresh.querySelectorAll("img"));
      var pending = 0, broken = 0;
      for (var i = 0; i < all.length; i++) {
        var n = all[i];
        if (n.hasAttribute("data-src")) { pending++; continue; }
        if (!n.complete) { pending++; continue; }
        if (!n.naturalWidth) { broken++; }
      }
      if (broken) return;                                   // stay hidden: something is wrong
      if (!pending) {
        fresh.setAttribute("data-recs-complete", "");
        fresh.style.display = "";          // clears the inline hide the server shipped
        // now that it has real dimensions, build the carousel
        var slider = fresh.querySelector("flickity-slider");
        if (slider && !slider.__recsSliderReady) {
          slider.__recsSliderReady = true;
          initSlider(slider);
        }
        try { window.dispatchEvent(new Event("resize")); } catch (e) {}   // nudge any instance that measured early
        return;
      }
      if (Date.now() > deadline) return;                    // stay hidden rather than show gaps
      setTimeout(check, 100);
    };
    check();
  }
  function deferImages(rail) {
    var imgs = rail.querySelectorAll("img[data-src]");
    if (!imgs.length) return;
    // a rail rendered while the drawer is already open (cart change) must not wait
    if (recsImgsUnlocked) { swapRailImages(); return; }
    if (window.__recsImgTriggers) return;
    window.__recsImgTriggers = 1;
    // Trigger: the DRAWER'S OWN OPEN STATE. An IntersectionObserver on the rail was the first
    // attempt and never fired — measured with the drawer class already "drawer animate
    // active", the rail's rect read x=1765 in a narrower viewport, so the open drawer is
    // still outside the viewport as IO computes it.
    var OPEN = /(^|\s)(active|is-open|open)(\s|$)/;
    var drawerEl = document.querySelector("cart-drawer");
    if (drawerEl && OPEN.test(drawerEl.className)) { unlockRailImages(); return; }
    if (drawerEl) {
      new MutationObserver(function () {
        if (OPEN.test(drawerEl.className)) unlockRailImages();
      }).observe(drawerEl, { attributes: true, attributeFilter: ["class"] });
    }
    // and start the moment the bag is clicked, so the images are in flight during the open
    // animation instead of after it
    document.addEventListener("click", function (e) {
      var t = e.target && e.target.closest && e.target.closest(
        'a[href="/cart"], a[href*="/cart"], #cart-icon-bubble, .header__icon--cart, [data-cart-drawer-toggle], form[action*="/cart/add"] [type="submit"]');
      if (t) unlockRailImages();
    }, true);
    document.addEventListener("cart:refresh", unlockRailImages);
    document.addEventListener("cart-drawer:open", unlockRailImages);
  }

  // ---- cart drawer: re-init the rail on cart change / drawer open ----
  // CRITICAL: the theme REPLACES the drawer's inner DOM on every real add-to-cart
  // (verified: #CartDrawer-CartItems and the rail are NEW nodes after a real ATC
  // click; only <cart-drawer> itself survives). So: never capture the rail or the
  // items node — observe the PERSISTENT <cart-drawer> and re-query the rail FRESH
  // on every refresh. Ignore mutations we cause ourselves (renders inside recs-rail).
  function wireCartDrawer(disc) {
    var primed = false;
    var drawer = document.querySelector("cart-drawer");
    // 2026-09-29: a SERVER-RENDERED drawer rail owns itself. The router used to claim any
    // recs-rail[data-recs-cart-drawer] and rebuild it via innerHTML — which threw away the
    // server HTML and re-assembled it client-side, so the SSR rail showed the client
    // fallback heading ("Recently viewed") over freshly-fetched tiles. If the markup came
    // from Liquid, leave it alone; the theme re-renders the whole drawer section on every
    // cart change, which keeps it in sync without us.
    // The SSR rail is checked PER DRAWER RENDER, not once at boot. It used to be decided
    // here, at page load — and on a product page with an EMPTY cart there is no rail yet, so
    // the router took its client branch and never bound anything. Add to bag, the drawer
    // re-rendered WITH the rail, and that rail got no JavaScript at all: no image swap (six
    // blank grey tiles, which is how Sebastian found it), no quick-add, no Flickity, no
    // pixels. Adding to an empty bag is the most common first touch on the site, so this was
    // the default experience, not an edge case.
    var bindSSR = function () {
      var rail = document.querySelector("recs-rail[data-recs-ssr]");
      if (!rail || rail.__recsBound) return false;
      rail.__recsBound = true;
      rail.setAttribute("data-recs-bound", "");   // so the document-level watch can see it cheaply
      // NOTE: the slider is NOT initialised here. The rail ships display:none and Flickity
      // measures whatever it is given — initialising inside a hidden element lays every cell
      // out at zero width, which is why Sebastian saw the heading and arrows over an empty
      // space on DESKTOP while mobile looked fine (mobile happened to re-measure on a resize
      // that desktop never fired). initSlider now runs in revealWhenComplete, once the rail
      // is actually visible and measurable.
      preloadQuickAdd(rail);
      wireEvents(rail, { slot: "cart-rail", rule_id: "ssr", source_type: "server_liquid", chain_depth: 0 });
      deferImages(rail);
      revealWhenComplete();
      return true;
    };
    if (drawer) {
      new MutationObserver(function () { bindSSR(); }).observe(drawer, { childList: true, subtree: true });
    }
    // ...and a watch that does NOT depend on that element surviving. Quick-add v8 replaces the
    // whole <cart-drawer> element, not just its children, which orphans the observer above:
    // on that path the drawer opened with a rail of six tiles and `__recsBound` was false, so
    // nothing bound, nothing swapped, and the rail stayed hidden (measured 2026-10-07). The
    // document-level observer is debounced and its guard is one attribute selector, so it
    // costs nothing until an unbound rail actually appears.
    var rebindTimer = null;
    new MutationObserver(function () {
      if (rebindTimer) return;
      rebindTimer = setTimeout(function () {
        rebindTimer = null;
        if (document.querySelector("recs-rail[data-recs-ssr]:not([data-recs-bound])")) bindSSR();
      }, 60);
    }).observe(document.documentElement, { childList: true, subtree: true });
    bindSSR();
    var ssr = document.querySelector("recs-rail[data-recs-ssr]");
    if (ssr) {
      // The rail's CONTENT is the theme's; the router still owns the interactions the
      // server cannot render — quick-add, the size overlay, and Flickity init — plus the
      // impression/click pixels. Bind them to the server markup and never rebuild it.
      // The drawer re-renders wholesale on every cart change, so re-bind on that too.
      return;
    }
    if (!drawer && !document.querySelector("recs-rail[data-recs-cart-drawer]")) return;
    var timer = null, lastKey = null, rendering = false;
    function refresh(force) {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var rail = document.querySelector("recs-rail[data-recs-cart-drawer]");  // fresh node every time
        if (!rail) return;
        // if a SERVER-rendered rail has arrived since boot (empty cart -> add to bag), it owns
        // itself: bind it and never run the client rebuild over the top of it.
        if (rail.hasAttribute("data-recs-ssr")) { bindSSR(); return; }
        fetchJSON("/cart.js").then(function (cart) {
          window.__recsCartHandles = (cart && cart.items || []).map(function (i) { return i.handle; });
          // Re-render ONLY when the cart's CONTENTS change. The observer fires on every
          // drawer mutation, and initRail rebuilds the rail via innerHTML — so a qty tick
          // (or the theme's own re-render) used to tear the rail down and rebuild it under
          // the cursor. Any click landing in that window hit a node being destroyed, which
          // is why the drawer felt like "I can ONLY interact with the carousel", and why it
          // then mysteriously worked once things settled. Nothing was ever blocking; the
          // DOM was moving. Keying on contents also skips a ~12x recs-card refetch per tick.
          var key = (cart && cart.items || []).map(function (i) { return i.handle + ":" + i.quantity; }).join("|");
          if (!force && key === lastKey && rail.hasAttribute("data-recs-ready")) return;
          if (rendering) return;
          lastKey = key;
          rendering = true;
          withCartGroups(cart)
            .then(function (c) { return initRail(rail, c, disc, { handles: {}, ygroups: {} }); })
            .then(function () { rendering = false; })
            .catch(function () { rendering = false; });
        });
      }, 250);
    }
    function isOurs(m) {
      var n = m.target && (m.target.nodeType === 1 ? m.target : m.target.parentElement);
      return !!(n && n.closest && n.closest("recs-rail"));
    }
    if (drawer) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (!isOurs(muts[i])) { primed = true; refresh(); return; }   // theme changed the drawer -> re-init
        }
      }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    }
    // 2026-09-28 PERF (Sebastian: "it's slow as fuck"). Measured on a plain DK homepage
    // view with the drawer NEVER opened: 10x ?view=recs-card (659ms avg, 163KB), 1x
    // recs-json, 4x cart.js — ~174KB and a 3.9s->5.4s chain, on EVERY pageview, for a rail
    // nobody had asked to see. The rail now hydrates on INTENT instead of on load: the
    // MutationObserver above already fires when the theme opens the drawer (it toggles
    // classes/children), and the delegated click below covers the cart triggers directly
    // so the fetch starts the instant the icon is clicked rather than after the open.
    function prime() { if (primed) return; primed = true; refresh(true); }
    document.addEventListener("click", function (e) {
      var t = e.target && e.target.closest && e.target.closest(
        'a[href="/cart"], a[href*="/cart"], #cart-icon-bubble, .header__icon--cart, [data-cart-drawer-toggle], form[action*="/cart/add"] [type="submit"]');
      if (t) prime();
    }, true);
    // an add-to-cart from anywhere also means the drawer is about to show
    document.addEventListener("cart:refresh", prime);
    document.addEventListener("cart-drawer:open", prime);
  }

  function boot() {
    persistUtm();
    var disc = window.__RECS_DISCOUNT__ || {};
    wireCartDrawer(disc);
    var rails = Array.prototype.slice.call(document.querySelectorAll("recs-rail:not([data-recs-cart-drawer])"));
    if (!rails.length) return;
    fetchJSON("/cart.js").then(function (cart) {
      window.__recsCartHandles = (cart && cart.items || []).map(function (i) { return i.handle; });
      return withCartGroups(cart);   // page rails read cart_state too — same promo truth
    }).then(function (cart) {
      // sequential so pageSeen dedupe is deterministic across page rails (PDP order).
      rails.reduce(function (chain, rail) {
        return chain.then(function () {
          return Promise.resolve().then(function () { return initRail(rail, cart, disc); })
            .catch(function (e) { if (window.console) console.warn("[recs] rail init failed", e); });
        });
      }, Promise.resolve());
    });
    // belt-and-braces: force-init any rail slider that a heavy/slow page left uninitialised
    function sweep() { Array.prototype.slice.call(document.querySelectorAll("recs-rail flickity-slider")).forEach(initSlider); }
    setTimeout(sweep, 1500); setTimeout(sweep, 4000);
  }

  // ---- quick add: "Add to bag" -> size overlay -> /cart/add.js (Visually parity, 2026-09-22) ----
  // Delegated on document: the theme replaces the drawer DOM on every cart change, so any
  // listener bound to a tile would die with it. Behaviour mirrors Visually's cart-drawer widget
  // (single-variant products add straight away; otherwise pick a size, then "Add to bag").
  // The add path is the theme's own, retrieved from Complete the Set: /cart/add.js with the
  // drawer's sections, then drawer.renderContents(state).
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
  // Warm the browser cache with each tile's small overlay image as soon as the drawer rail
  // renders, so opening the size overlay never shows an image still loading.
  var qaPreloaded = {};
  function preloadQuickAdd(rail) {
    Array.prototype.forEach.call(rail.querySelectorAll("[data-recs-variants]"), function (n) {
      try { var u = JSON.parse(n.textContent).image; if (u && !qaPreloaded[u]) { qaPreloaded[u] = 1; (new Image()).src = u; } } catch (e) {}
    });
  }
  function closeQuickAdd() { var o = document.querySelector(".recs-qa"); if (o) o.parentNode.removeChild(o); }
  function addToCart(variantId, btn, tile) {
    var drawer = document.querySelector("cart-drawer");
    var ids = (drawer && drawer.getSectionsToRender) ? drawer.getSectionsToRender().map(function (x) { return x.id; }) : [];
    var rail = tile && tile.closest("recs-rail");
    if (btn) { btn.disabled = true; btn.textContent = "..."; }
    return fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: ids, sections_url: location.pathname }) })
      .then(function (r) { if (!r.ok) throw new Error("add " + r.status); return r.json(); })
      .then(function (state) {
        if (btn) btn.textContent = "Item Added!";
        emit("rec_add_to_cart", Object.assign({ product: tile && tile.getAttribute("data-recs-handle"), variant_id: variantId },
          (rail && rail.__recsMeta) || {}));
        setTimeout(function () {
          closeQuickAdd();
          if (drawer && drawer.renderContents) { try { drawer.renderContents(state); return; } catch (e) {} }
          window.location.href = "/cart";
        }, 500);
      })
      .catch(function (e) {
        if (btn) { btn.disabled = false; btn.textContent = "Try Again :("; }
        if (window.console) console.warn("[recs] quick add failed", e);
      });
  }
  function openQuickAdd(tile) {
    var node = tile.querySelector("[data-recs-variants]");
    var data; try { data = JSON.parse(node.textContent); } catch (e) { return; }
    var vs = (data.variants || []).filter(function (v) { return v.id; });
    var live = vs.filter(function (v) { return v.available; });
    if (vs.length === 1 && live.length === 1) return addToCart(live[0].id, tile.querySelector("[data-recs-atc]"), tile);
    closeQuickAdd();
    // Reserve the image box at the product's real aspect ratio (no reflow when it lands) and
    // reuse the image the tile has ALREADY loaded (served from cache -> no loading flash).
    var tileImg = tile.querySelector(".card__media img");
    // prefer the small image preloaded when the rail rendered (preloadQuickAdd); fall back
    // to the tile's own image only if it has fully loaded
    var qaImg = data.image || (tileImg && tileImg.complete && (tileImg.currentSrc || tileImg.src));
    var qaImgH = Math.round(74 / (Number(data.image_ratio) || 0.8));
    var rail = tile.closest("recs-rail");
    var o = document.createElement("div");
    o.className = "recs-qa";
    o.innerHTML =
      '<div class="recs-qa__panel" role="dialog" aria-label="' + esc(data.title) + '">' +
        '<button type="button" class="recs-qa__close" aria-label="Close" data-recs-qa-close>' +
          '<svg viewBox="0 0 13 13" width="13" height="13" aria-hidden="true"><path d="M1 1l11 11M12 1L1 12" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
        '<div class="recs-qa__head">' +
          (qaImg ? '<img class="recs-qa__img" src="' + esc(qaImg) + '" alt="" width="74" height="' + qaImgH + '" style="height:' + qaImgH + 'px">' : "") +
          '<div><div class="recs-qa__name">' + esc(data.title) + '</div>' +
          '<div class="recs-qa__price">' + esc(data.price) + (data.compare ? ' <s>' + esc(data.compare) + '</s>' : "") + '</div></div>' +
        '</div>' +
        (data.option ? '<div class="recs-qa__label">' + esc(data.option) + '</div>' : "") +
        '<div class="recs-qa__opts">' + vs.map(function (v) {
          return '<button type="button" class="recs-qa__opt' + (v.available ? "" : " is-oos") + '"' + (v.available ? "" : " disabled") +
                 ' data-vid="' + v.id + '">' + esc(v.label) + '</button>';
        }).join("") + '</div>' +
        '<button type="button" class="recs-qa__add" data-recs-qa-add disabled>Add to bag</button>' +
      '</div>';
    // The drawer has a sticky totals/checkout footer; make sure the panel is actually in view.
    function reveal() { try { o.querySelector(".recs-qa__panel").scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch (e) {} }
    // Show the overlay only once its image is decoded (it is preloaded, so this is a few ms);
    // inserting then decoding flashed an empty image box. Capped so a slow image never blocks.
    var im = o.querySelector(".recs-qa__img");
    if (im && !im.complete) {
      o.style.visibility = "hidden";
      var shown = false, show = function () { if (!shown) { shown = true; o.style.visibility = ""; reveal(); } };
      (im.decode ? im.decode() : Promise.reject()).then(show, show);
      setTimeout(show, 300);
    }
    (rail || document.body).appendChild(o);
    o.__tile = tile;
    if (o.style.visibility !== "hidden") reveal();
  }
  document.addEventListener("click", function (ev) {
    var t = ev.target;
    if (!t.closest) return;
    var atc = t.closest("[data-recs-atc]");
    if (atc) { ev.preventDefault(); ev.stopPropagation(); openQuickAdd(atc.closest(".recs-tile")); return; }
    var qa = t.closest(".recs-qa");
    if (!qa) return;
    ev.preventDefault(); ev.stopPropagation();
    if (t.closest("[data-recs-qa-close]") || t === qa) return closeQuickAdd();
    var opt = t.closest(".recs-qa__opt");
    if (opt && !opt.disabled) {
      Array.prototype.forEach.call(qa.querySelectorAll(".recs-qa__opt"), function (b) { b.classList.remove("is-sel"); });
      opt.classList.add("is-sel");
      qa.querySelector("[data-recs-qa-add]").disabled = false;
      return;
    }
    var add = t.closest("[data-recs-qa-add]");
    if (add && !add.disabled) {
      var sel = qa.querySelector(".recs-qa__opt.is-sel");
      if (sel) addToCart(Number(sel.getAttribute("data-vid")), add, qa.__tile);
    }
  }, true);
  document.addEventListener("keydown", function (ev) { if (ev.key === "Escape") closeQuickAdd(); });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // expose for tests / debugging
  window.__recs = { evaluateRuleset: evaluateRuleset, matchCond: matchCond };
})();
